// Decorative cook-step illustrations (boil / stir / brown / bloom / toss
// / plate). Each is a small SVG-based loop tied to a `step.animation`
// hint in the recipe. Pure visual; zero coupling to cook state.
//
// Extracted from CookMode.jsx 2026-05-03 to keep the parent below the
// file-size tripwire. Import { AnimationMap } and key by the step's
// animation string — undefined hints render nothing.

import { useState, useEffect } from "react";
import { COLOR } from "../lib/tokens";

function BoilAnimation() {
  return (
    <div style={{ position:"relative", width:120, height:120, margin:"0 auto" }}>
      <svg viewBox="0 0 120 120" style={{ width:"100%", height:"100%" }}>
        <rect x="25" y="60" width="70" height="45" rx="6" fill={COLOR.border} />
        <rect x="20" y="58" width="80" height="10" rx="4" fill={COLOR.border} />
        <rect x="10" y="60" width="12" height="6" rx="3" fill={COLOR.sub} />
        <rect x="98" y="60" width="12" height="6" rx="3" fill={COLOR.sub} />
        <rect x="27" y="68" width="66" height="35" rx="4" fill="#1a6fa0" opacity="0.8" />
        {[40,55,70,85].map((x,i) => (
          <circle key={i} cx={x} cy={85} r={3} fill="#7dd3fc" opacity="0.7">
            <animateMotion dur={`${1.2+i*0.2}s`} repeatCount="indefinite" path={`M0,0 Q${i%2===0?5:-5},${-15} 0,-30`} />
          </circle>
        ))}
        {[45,60,75].map((x,i) => (
          <path key={i} d={`M${x},58 Q${x+5},50 ${x},42 Q${x-5},34 ${x},26`} stroke="#e0e0e0" strokeWidth="2" fill="none" opacity="0.4"
            style={{ animation:`steam 2s ${i*0.4}s infinite` }} />
        ))}
      </svg>
      <style>{`@keyframes steam{0%,100%{opacity:0;transform:translateY(0)}50%{opacity:0.5;transform:translateY(-8px)}}`}</style>
    </div>
  );
}

function StirAnimation() {
  const [angle, setAngle] = useState(0);
  useEffect(() => { const id = setInterval(() => setAngle(a => (a+8)%360),40); return () => clearInterval(id); },[]);
  const r=28, cx=60, cy=70, rad=(angle*Math.PI)/180;
  const tipX=cx+r*Math.sin(rad), tipY=cy-r*Math.cos(rad)*0.4;
  return (
    <div style={{ width:120, height:120, margin:"0 auto" }}>
      <svg viewBox="0 0 120 120">
        <ellipse cx="60" cy="75" rx="40" ry="28" fill={COLOR.border} />
        <ellipse cx="60" cy="68" rx="38" ry="22" fill="#c9a96e" opacity="0.9" />
        {[0,1,2,3].map(i=>(
          <ellipse key={i} cx={55+i*4} cy={68} rx={8} ry={3} fill="#f5deb3" opacity="0.6"
            style={{ transform:`rotate(${angle+i*45}deg)`, transformOrigin:"60px 68px" }} />
        ))}
        <line x1={cx} y1={cy} x2={tipX} y2={tipY} stroke={COLOR.dim} strokeWidth="3" strokeLinecap="round" />
        <line x1={cx} y1={cy} x2={cx+(cx-tipX)*0.3} y2={cy-25} stroke={COLOR.dim} strokeWidth="4" strokeLinecap="round" />
        <ellipse cx={tipX} cy={tipY} rx={5} ry={3.5} style={{ transform:`rotate(${angle}deg)`, transformOrigin:`${tipX}px ${tipY}px` }} fill={COLOR.dim} />
      </svg>
    </div>
  );
}

function BrownAnimation() {
  const [phase, setPhase] = useState(0);
  useEffect(() => { const id = setInterval(() => setPhase(p=>(p+1)%100),80); return () => clearInterval(id); },[]);
  const color=`hsl(${40-phase*0.15},${70+phase*0.3}%,${70-phase*0.35}%)`;
  return (
    <div style={{ width:120, height:120, margin:"0 auto" }}>
      <svg viewBox="0 0 120 120">
        <ellipse cx="60" cy="72" rx="48" ry="32" fill={COLOR.soil} />
        <ellipse cx="60" cy="65" rx="42" ry="26" fill={color} />
        {phase>20 && [35,50,70,82].map((x,i)=>(
          <circle key={i} cx={x} cy={65+(i%2)*4} r={2.5} fill="white" opacity={0.3+(phase/200)}>
            <animate attributeName="r" values="2;4;2" dur="1.5s" repeatCount="indefinite" />
          </circle>
        ))}
      </svg>
    </div>
  );
}

function BloomAnimation() {
  return (
    <div style={{ width:120, height:120, margin:"0 auto" }}>
      <svg viewBox="0 0 120 120">
        <ellipse cx="60" cy="72" rx="44" ry="28" fill="#2a1800" />
        <ellipse cx="60" cy="65" rx="38" ry="20" fill="#6b3a0a" />
        {[...Array(12)].map((_,i)=>{const a=(i/12)*Math.PI*2,r=14+(i%3)*4; return (
          <circle key={i} cx={60+r*Math.cos(a)} cy={65+r*0.5*Math.sin(a)} r={2+(i%2)} fill="#1a0a00" opacity="0.8"
            style={{ animation:`pepperPop 2s ${i*0.15}s infinite alternate` }} />
        );})}
        <style>{`@keyframes pepperPop{0%{opacity:0.4}100%{opacity:1}}`}</style>
      </svg>
    </div>
  );
}

function TossAnimation() {
  const [t, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT(p=>p+1),50); return () => clearInterval(id); },[]);
  const wave=Math.sin(t/8)*6;
  return (
    <div style={{ width:120, height:120, margin:"0 auto" }}>
      <svg viewBox="0 0 120 120">
        <ellipse cx="60" cy="78" rx="44" ry="26" fill={COLOR.soil} />
        {[...Array(8)].map((_,i)=>{const base=65+Math.sin((t+i*15)/8)*4,x=30+i*9; return (
          <path key={i} d={`M${x},${base+wave*0.5} Q${x+4},${base-8+wave} ${x+8},${base+wave*0.5}`}
            stroke="#f5deb3" strokeWidth="2.5" fill="none" opacity="0.85" strokeLinecap="round" />
        );})}
        {[40,55,70,82].map((x,i)=>(
          <circle key={i} cx={x+Math.sin((t+i*20)/8)*3} cy={62+Math.cos((t+i*20)/8)*4} r={2} fill="#f0e68c" opacity="0.7" />
        ))}
      </svg>
    </div>
  );
}

function PlateAnimation() {
  return (
    <div style={{ width:120, height:120, margin:"0 auto" }}>
      <svg viewBox="0 0 120 120">
        <circle cx="60" cy="68" r="44" fill={COLOR.ink} />
        <circle cx="60" cy="68" r="36" fill="#e8e2d8" />
        {[...Array(6)].map((_,i)=>{const a=(i/6)*Math.PI*2; return (
          <path key={i} d={`M60,68 Q${60+16*Math.cos(a-0.4)},${68+10*Math.sin(a-0.4)} ${60+20*Math.cos(a)},${68+12*Math.sin(a)}`}
            stroke="#d4a853" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.8" />
        );})}
        <circle cx="60" cy="55" r="5" fill="#daa520" opacity="0.6" />
      </svg>
    </div>
  );
}

export const AnimationMap = {
  boil:  BoilAnimation,
  stir:  StirAnimation,
  brown: BrownAnimation,
  bloom: BloomAnimation,
  toss:  TossAnimation,
  plate: PlateAnimation,
};
